// path: src/Leaderboards.js
// Endless personal-best leaderboards (signed-in).

import React, { useEffect, useMemo } from "react";
import Login from "./Login";
import {
    LEADERBOARD_BOARD_LABELS,
    LEADERBOARD_BOARD_SIZES,
    LEADERBOARD_CATEGORIES,
} from "./utils/leaderboards";
import { formatGid, isValidGid } from "./utils/gid";
import "./App.css";

const Leaderboards = ({
    isAuthenticated = false,
    user = null,
    entries = [],
    myEntry = null,
    loading = false,
    error = "",
    categoryId = "endless_level",
    boardSize = 4,
    onChangeCategory,
    onChangeBoardSize,
    onRefresh,
    onLoginSuccess,
    onViewProfile,
}) => {
    const category = useMemo(
        () =>
            LEADERBOARD_CATEGORIES.find((item) => item.id === categoryId) ||
            LEADERBOARD_CATEGORIES[0],
        [categoryId]
    );

    const myRankInList = useMemo(() => {
        if (!user?.uid) {
            return null;
        }
        const index = entries.findIndex((entry) => entry.uid === user.uid);
        return index >= 0 ? index + 1 : null;
    }, [entries, user]);

    useEffect(() => {
        if (isAuthenticated && typeof onRefresh === "function") {
            onRefresh();
        }
    }, [isAuthenticated, categoryId, boardSize]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="leaderboardsScreen">
            <div className="friendsHeader">
                <div>
                    <h1 className="impactFont pageTitle friendsTitle">Leaderboards</h1>
                    <p className="friendsBody" style={{ marginBottom: 0 }}>
                        Signed-in personal bests across Endless boards.
                    </p>
                </div>
            </div>

            {!isAuthenticated || !user ? (
                <div className="friendsPanel">
                    <h2 className="friendsSectionTitle">Sign in required</h2>
                    <p className="friendsBody">
                        Leaderboards are only available on a signed-in account.
                    </p>
                    <div className="friendsActions">
                        <Login onSuccess={onLoginSuccess} />
                    </div>
                </div>
            ) : (
                <>
                    <div className="friendsTabs" role="tablist" aria-label="Leaderboard categories">
                        {LEADERBOARD_CATEGORIES.map((item) => (
                            <button
                                key={item.id}
                                type="button"
                                role="tab"
                                aria-selected={categoryId === item.id}
                                className={`friendsTab ${
                                    categoryId === item.id ? "friendsTab--active" : ""
                                }`}
                                onClick={() => onChangeCategory(item.id)}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>

                    <div className="leaderboardSizeRow" role="tablist" aria-label="Board sizes">
                        {LEADERBOARD_BOARD_SIZES.map((size) => (
                            <button
                                key={size}
                                type="button"
                                className={`friendsTab ${
                                    boardSize === size ? "friendsTab--active" : ""
                                }`}
                                onClick={() => onChangeBoardSize(size)}
                            >
                                {LEADERBOARD_BOARD_LABELS[size]}
                            </button>
                        ))}
                    </div>

                    <div className="friendsPanel">
                        <h2 className="friendsSectionTitle">
                            {category.label} · {LEADERBOARD_BOARD_LABELS[boardSize]}
                        </h2>
                        <p className="friendsBody">{category.description}</p>

                        {myEntry ? (
                            <div className="leaderboardMyBest">
                                Your best: {myEntry.value}
                                {myRankInList ? ` · Rank #${myRankInList}` : " · Outside top list"}
                            </div>
                        ) : (
                            <div className="leaderboardMyBest">
                                Your best: — (play Endless to set a personal best)
                            </div>
                        )}

                        {error ? (
                            <div className="friendsBody friendsBody--error">{error}</div>
                        ) : null}

                        {loading ? (
                            <div className="friendsBody">Loading leaderboard…</div>
                        ) : entries.length === 0 ? (
                            <div className="friendsBody">
                                No scores yet for this board. Be the first!
                            </div>
                        ) : (
                            <div className="leaderboardList" role="list">
                                {entries.map((entry, index) => {
                                    const isMe = user?.uid === entry.uid;
                                    const canOpen =
                                        !isMe &&
                                        typeof onViewProfile === "function" &&
                                        entry.uid;
                                    const RowTag = canOpen ? "button" : "div";
                                    return (
                                        <RowTag
                                            key={entry.uid}
                                            type={canOpen ? "button" : undefined}
                                            className={`friendsListRow leaderboardRow ${
                                                isMe ? "leaderboardRow--me" : ""
                                            }${
                                                canOpen
                                                    ? " leaderboardRow--clickable"
                                                    : ""
                                            }`}
                                            role="listitem"
                                            aria-label={
                                                canOpen
                                                    ? `View ${
                                                          entry.username ||
                                                          "Player"
                                                      }'s profile`
                                                    : undefined
                                            }
                                            onClick={
                                                canOpen
                                                    ? () =>
                                                          onViewProfile(
                                                              entry.uid
                                                          )
                                                    : undefined
                                            }
                                        >
                                            <div className="leaderboardRank">#{index + 1}</div>
                                            <div className="friendsListMeta">
                                                <div
                                                    className={`friendsListName${
                                                        canOpen
                                                            ? " friendsListName--link"
                                                            : ""
                                                    }`}
                                                >
                                                    {entry.username || "Player"}
                                                    {isMe ? " (you)" : ""}
                                                </div>
                                                <div className="friendsListSub">
                                                    {isValidGid(entry.gid)
                                                        ? `GID ${formatGid(entry.gid)}`
                                                        : "GID —"}
                                                </div>
                                            </div>
                                            <div className="leaderboardValue">
                                                {entry.value}
                                                <span className="leaderboardValueLabel">
                                                    {category.valueLabel}
                                                </span>
                                            </div>
                                        </RowTag>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default Leaderboards;
